// Copyright 2016 Sorama B.V.

import { bindable } from "aurelia-framework";
import { IPlotParent } from "controls/plot/IPlotParent";
import { IPlotPart, Margin, Pair, Rect, Size } from "controls/plot/IPlotPart";
import { Unit } from "units/Unit";

export abstract class PlotParentBase implements IPlotParent {
    //#region HTML Elements
    @bindable public controlContainer: HTMLElement;
    @bindable public cursorClass = "auto";
    public plotElement: Element;
    public inputElement: HTMLElement;
    //#endregion

    //#region Fields

    protected xAxisStart = 0;
    protected xAxisEnd = 1;
    protected yAxisStart = 0;
    protected yAxisEnd = 1;

    protected xUnit: Unit;
    protected yUnit: Unit;

    protected minzoom = 0.01;

    protected margin: Margin = new Margin(0, 0, 0, 0);
    protected size: Size = new Size(0, 0);

    protected viewPort: Rect = new Rect(0, 0, 1, 1);
    protected viewPortBounds: Rect = new Rect(0, 0, 1, 1);

    protected plotParts: IPlotPart[] = [];

    //#endregion

    //#region Constructor and Initialisation

    public bind(context: unknown): void {}

    public attached(): void {
        if (!this.controlContainer) {
            throw new Error("controlContainer is not defined.");
        }

        if (!this.plotElement) {
            throw new Error("plotElement is not defined.");
        }

        if (!this.inputElement) {
            throw new Error("inputElement is not defined.");
        }
    }

    public detached(): void {}

    //#endregion

    //#region IPlotParent Members
    public subscribePart(part: IPlotPart) {
        this.plotParts.push(part);

        this.requestMeasure(true);

        // Parts do not influence viewport, do not remeasure but just notify plotpart of viewport
        part.onViewPortComputed(this.viewPort, this.viewPortBounds, new Pair<Unit>(this.xUnit, this.yUnit));
    }

    public unSubscribePart(part: IPlotPart) {
        const index = this.plotParts.indexOf(part);
        if (index === -1) return;

        this.plotParts.splice(index, 1);
        this.requestMeasure(true);
    }

    public addInputEventListener(
        eventName: string,
        eventHandler: (ev: MouseEvent | TouchEvent) => void,
        options?: unknown
    ): void {
        if (this.inputElement) {
            this.inputElement.addEventListener(eventName, eventHandler, options);
        }
    }

    public removeInputEventListener(eventName: string, eventHandler: EventListener): void {
        if (this.inputElement) {
            this.inputElement.removeEventListener(eventName, eventHandler);
        }
    }

    public addWindowInputEventListener(eventName: string, eventHandler: EventListener, options?: unknown): void {
        window.addEventListener(eventName, eventHandler, options);
    }

    public removeWindowInputEventListener(eventName: string, eventHandler: EventListener): void {
        window.removeEventListener(eventName, eventHandler);
    }

    public setViewPort(rect: Rect): void {
        // Clip the new rect so our view never goes beyond our data range
        if (rect.width > this.viewPortBounds.width) {
            rect.x = this.viewPortBounds.x;
            rect.width = this.viewPortBounds.width;
        } else if (rect.x < this.viewPortBounds.x) {
            rect.x = this.viewPortBounds.x;
        } else if (rect.x + rect.width > this.viewPortBounds.x + this.viewPortBounds.width) {
            rect.x = this.viewPortBounds.x + this.viewPortBounds.width - rect.width;
        }

        if (rect.height > this.viewPortBounds.height) {
            rect.y = this.viewPortBounds.y;
            rect.height = this.viewPortBounds.height;
        } else if (rect.y < this.viewPortBounds.y) {
            rect.y = this.viewPortBounds.y;
        } else if (rect.y + rect.height > this.viewPortBounds.y + this.viewPortBounds.height) {
            rect.y = this.viewPortBounds.y + this.viewPortBounds.height - rect.height;
        }

        // Set the new start and endpoints for the (zoomed in) axis
        this.xAxisStart = rect.x;
        this.xAxisEnd = rect.x + rect.width;
        this.yAxisStart = rect.y;
        this.yAxisEnd = rect.y + rect.height;

        // Re-measure the viewport (applies new values for x and y axis start/end to the viewport) and data
        this.requestViewPort();
    }

    public abstract requestMeasure(force: boolean): void;

    public abstract requestViewPort(): void;

    public abstract roundToNearestBin(value: number, axis: string): number;

    //#endregion
}
